/**
 * Syntax-highlighted code block with click-to-preview for embedded
 * knowledge references.
 *
 * One reference shape is recognised inside the rendered Prism tokens:
 *
 *  - Concept group IDs — `<digits>-<org-suffix>` (e.g. `3032889331786151605-Verantos`,
 *    `0081754993858763-qa-org`). Click opens `ConceptGroupDetailsDialog`.
 *
 * analytical variable slug auto-linking was removed: SQL identifiers (table
 * aliases, CTE names, join aliases) routinely collide with the slug's
 * snake_case structural pattern, and Prism's SQL grammar doesn't tokenize
 * plain identifiers consistently enough to reliably tell a real knowledge
 * reference apart from a query-local name. See Markdown.tsx for the
 * (non-SQL) prose-context AV slug linking, which doesn't have this problem.
 *
 * Implementation note: we don't override Prism's renderer — instead we let
 * it produce its normal `.token.string` / `.token.string-property` spans
 * and post-process them via a useEffect+ref to flag matches as clickable.
 * Click handling is delegated on the container, so re-renders don't have
 * to detach handlers per-token.
 */

import { Box } from '@mui/material';
import type { CSSProperties, MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { ConceptGroupDetailsDialog } from '@verantos-dev/feature-concept-groups-list';

import { useActiveDataset } from '../context/ActiveDatasetContext';

const CONCEPT_GROUP_ID_PATTERN = /^\d{4,}-[A-Za-z][A-Za-z0-9_-]*$/;

const stripQuotes = (value: string): string => value.replace(/^['"`]|['"`]$/g, '');

interface CodeWithKnowledgeLinksProps {
  code: string;
  language: string;
  customStyle?: CSSProperties;
  wrapLongLines?: boolean;
}

export const CodeWithKnowledgeLinks = ({
  code,
  language,
  customStyle,
  wrapLongLines,
}: CodeWithKnowledgeLinksProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [openConceptGroupId, setOpenConceptGroupId] = useState<string | null>(null);
  // TODO: cleanup with concept_group_prevalence_preview ff
  const activeDataset = useActiveDataset();

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    // Walk leaf spans (no element children). This is more robust than
    // relying on Prism's exact token classnames, which differ between
    // languages and can collide across builds. We only mark spans that
    // bottom out at a single text node so we don't accidentally match
    // a parent whose concatenated children happen to look like an id.
    const spans = root.querySelectorAll<HTMLElement>('span');
    spans.forEach((span) => {
      delete span.dataset.conceptGroupId;
      if (span.children.length > 0) return;
      const raw = stripQuotes((span.textContent ?? '').trim());
      if (!raw) return;
      if (CONCEPT_GROUP_ID_PATTERN.test(raw)) {
        span.style.cursor = 'pointer';
        span.style.textDecoration = 'underline';
        span.style.textUnderlineOffset = '2px';
        span.dataset.conceptGroupId = raw;
      }
    });
  });

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    let el: HTMLElement | null = event.target as HTMLElement;
    while (el && el !== event.currentTarget) {
      if (el.dataset?.conceptGroupId) {
        setOpenConceptGroupId(el.dataset.conceptGroupId);
        return;
      }
      el = el.parentElement;
    }
  };

  return (
    <Box
      ref={containerRef}
      onClick={handleClick}
      sx={{
        // The `oneLight` Prism theme renders comments at #a0a1a7 (~3.0:1
        // contrast on white) which fails WCAG AA. Override the comment
        // tokens to neutral-600 (~5.7:1) so the SQL preamble is actually
        // readable. Italics are preserved for visual separation.
        //
        // `!important` is required because `react-syntax-highlighter` applies
        // the theme's color as an INLINE `style={{ color: '#a0a1a7' }}` on
        // each comment span — inline styles beat normal stylesheet rules,
        // even injected via sx. Without `!important` the override silently
        // does nothing.
        '& .token.comment, & .token.prolog, & .token.doctype, & .token.cdata': {
          color: (theme) => `${theme.palette.text.muted} !important`,
          fontStyle: 'italic !important',
        },
      }}
    >
      <SyntaxHighlighter
        language={language}
        wrapLongLines={wrapLongLines}
        style={oneLight}
        customStyle={customStyle}
      >
        {code}
      </SyntaxHighlighter>
      {openConceptGroupId && (
        <ConceptGroupDetailsDialog
          conceptGroupId={openConceptGroupId}
          datasetReleaseId={activeDataset?.id ?? null}
          datasetReleaseDisplayName={activeDataset?.displayName ?? null}
          onClose={() => setOpenConceptGroupId(null)}
        />
      )}
    </Box>
  );
};
